import React, { useRef, useEffect } from 'react';
import { END_OF_LIST } from './constants';

interface VoucherRightPanelProps {
  currentListContext: { title: string; items: string[]; query: string; showCreate: boolean };
  currentList: string[];
  selectedIndex: number;
  voucherType: string;
  derivedJournalTypes: Set<string>;
  ledgerDb: Record<string, any>;
  itemDb: Record<string, any>;
  focusedField: string;
  baseCurrency?: any;
  drTotal: number;
  crTotal: number;
  isBalanced: boolean;
  saveError: string | null;
  handleListClick: (item: string) => void;
  triggerCreateMenu: () => void;
}

const HELP_MAP: Record<string, { title: string; text: string }> = {
  drcr:    { title: 'Dr / Cr',   text: 'Type D for Dr, C for Cr' },
  qty:     { title: 'Quantity',  text: 'Enter quantity (e.g. 5, 2.5)' },
  rate:    { title: 'Rate',      text: 'Enter rate per unit (e.g. 1500.00)' },
  debit:   { title: 'Debit',    text: 'Enter debit amount' },
  credit:  { title: 'Credit',   text: 'Enter credit amount' },
  amount:  { title: 'Amount',   text: 'Enter amount (e.g. 25000.00, -0.50)' },
};

export const VoucherRightPanel: React.FC<VoucherRightPanelProps> = ({
  currentListContext, currentList, selectedIndex, voucherType, derivedJournalTypes,
  ledgerDb, itemDb, focusedField, baseCurrency, drTotal, crTotal, isBalanced,
  saveError, handleListClick, triggerCreateMenu,
}) => {
  const fmt = (n: number) =>
    n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      selectedRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const getHelpCard = () => {
    if (focusedField === 'voucherDate') return { title: 'Voucher Date', text: 'Change voucher date (Press F2)' };
    if (focusedField === 'narration')   return { title: 'Narration', text: 'Brief description for this voucher' };
    const col = focusedField.split('_').pop() || '';
    return HELP_MAP[col] || { title: 'Tip', text: '↑↓ to select · ↑ past top = Create · Alt+C to create new' };
  };

  return (
    <div className="w-[30%] h-full min-h-0 bg-[#eef6ea] flex flex-col border-l border-[#aebec8] shadow-inner shrink-0 overflow-hidden">
      <div className="bg-[#1e8585] text-white font-semibold text-[13px] px-2 py-[3px]">
        {currentListContext.title || 'Context Menu'}
      </div>

      {saveError && (
        <div className="bg-[#ffd6d6] border-b border-red-300 px-3 py-2 shrink-0">
          <div className="text-red-700 font-bold text-[12px] mb-[2px]">Error Detected</div>
          <div className="text-red-600 text-[11px] font-medium">{saveError}</div>
        </div>
      )}

      {currentListContext.title ? (
        <>
          {currentListContext.showCreate && (
            <div
              className={`flex items-center justify-between px-2 py-[4px] text-[13px] font-semibold border-b border-[#aebec8] cursor-pointer select-none transition-colors ${selectedIndex === -1 ? 'bg-[#1e8585] text-white' : 'text-[#1e8585] hover:bg-[#d9e3eb]'}`}
              onMouseDown={(e) => { e.preventDefault(); triggerCreateMenu(); }}
              title="Alt+C"
            >
              <span className="underline">Create</span>
              <span className={`text-[10px] font-normal ${selectedIndex === -1 ? 'text-white opacity-80' : 'text-gray-400'}`}>Alt+C</span>
            </div>
          )}
          <div className="flex-1 overflow-y-auto pb-4" ref={listRef}>
            {currentList.length > 0 ? currentList.map((item, index) => {
              const isEoL = item === END_OF_LIST;
              const isSelected = index === selectedIndex;
              return (
                <div
                  key={item}
                  ref={isSelected ? selectedRef : null}
                  onMouseDown={(e) => { e.preventDefault(); handleListClick(item); }}
                  className={`px-2 text-[13px] py-[3px] cursor-pointer flex justify-between items-center ${isSelected ? 'bg-[#1e8585] text-white font-semibold active-row' : 'text-black'}`}
                >
                  <span className={isEoL ? (isSelected ? 'font-bold text-white' : 'font-bold text-[#b94040]') : ''}>
                    {item}
                  </span>
                  {!isEoL && (
                    <span className={`text-[10px] ml-1 font-semibold ${isSelected ? 'text-white/80' : ''}`}>
                      {currentListContext.title.includes('Stock') && itemDb[item]
                        ? <span className={`px-1.5 py-[1px] rounded text-[9px] ${isSelected ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                            {itemDb[item].stock}
                          </span>
                        : <span className="opacity-60 italic">{ledgerDb[item]?.balance || ''}</span>}
                    </span>
                  )}
                </div>
              );
            }) : (
              <div className="px-2 text-[13px] py-[4px] text-[#1e8585] italic">Spelling Error — No matches found</div>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 p-4 flex flex-col items-center justify-start pt-10 gap-2">
          <div className="text-[#1e8585] font-bold text-[13px]">{getHelpCard().title}</div>
          <div className="text-black text-[12px] text-center italic">{getHelpCard().text}</div>
        </div>
      )}

      {derivedJournalTypes.has(voucherType) && (drTotal > 0 || crTotal > 0) && !saveError && (
        <div className={`border-t text-[11px] px-3 py-2 font-semibold shrink-0 ${isBalanced ? 'bg-[#d4edda] text-green-800 border-green-300' : 'bg-[#fff3cd] text-yellow-800 border-yellow-300'}`}>
          {isBalanced
            ? `✓ ${voucherType} balanced — ${baseCurrency?.symbol || ''} ${fmt(drTotal)}`.trim()
            : `Diff: ${baseCurrency?.symbol || ''} ${fmt(Math.abs(drTotal - crTotal))} (${drTotal > crTotal ? 'Cr' : 'Dr'} remaining)`.trim()}
        </div>
      )}
    </div>
  );
};
